import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Tag } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { seo } from "../translations/seo";
import { useLanguage } from "../context/LanguageContext";
import { localizePost, DATE_LOCALES } from "../utils/blog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDate(isoStr, lang) {
  if (!isoStr) return "";
  try {
    return new Date(isoStr).toLocaleDateString(DATE_LOCALES[lang] || "fr-BE", { day: "numeric", month: "long", year: "numeric" });
  } catch { return ""; }
}

export default function Blog() {
  const { t, language } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    axios.get(`${API}/blog?limit=50`)
      .then((r) => setPosts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const localized = posts.map((p) => localizePost(p, language));
  const categories = ["all", ...Array.from(new Set(localized.map((p) => p.category)))];
  const filtered = activeCategory === "all" ? localized : localized.filter((p) => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-white font-jakarta">
      <Navbar />
      <Seo title={seo[language].blog.title} description={seo[language].blog.description} />

      {/* Hero */}
      <div className="pt-28 pb-16 bg-gradient-to-br from-slate-50 to-blue-50/40" data-testid="blog-hero">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 text-center">
          <span className="text-[#3B9DA5] text-sm font-semibold uppercase tracking-widest">
            {t.blog.subtitle}
          </span>
          <h1 className="font-outfit text-5xl sm:text-6xl font-bold text-slate-900 mt-3">
            {t.blog.title}
          </h1>
        </div>
      </div>

      {/* Category Filter */}
      <div className="sticky top-16 z-20 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-3 flex gap-2 overflow-x-auto scrollbar-hide" data-testid="category-filter">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`flex-shrink-0 text-sm font-semibold px-4 py-2 rounded-full transition-all ${
                activeCategory === cat
                  ? "gradient-bg text-white shadow-sm"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              data-testid={`filter-${cat}`}
            >
              {cat === "all" ? t.blog.all : cat}
            </button>
          ))}
        </div>
      </div>

      {/* Posts */}
      <section className="py-16 pb-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="blog-loading">
              {[1, 2, 3].map((i) => (
                <div key={i} className="rounded-3xl bg-slate-100 h-72 animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-slate-500 py-12" data-testid="blog-empty">
              {t.blog.no_posts}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-list">
              {filtered.map((post) => (
                <Link
                  key={post.id}
                  to={`/actualites/${post.slug}`}
                  className="group bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  data-testid={`blog-post-${post.id}`}
                >
                  <div className="overflow-hidden h-48 bg-slate-100">
                    {post.image ? (
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full gradient-bg opacity-20" />
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Tag className="w-3 h-3 text-[#3B9DA5]" />
                      <span className="text-[#2E4DB5] text-xs font-semibold uppercase tracking-wide">
                        {post.category}
                      </span>
                      <span className="text-slate-300">•</span>
                      <span className="text-slate-400 text-xs">{formatDate(post.published_at, language)}</span>
                    </div>
                    <h2 className="font-outfit font-bold text-slate-900 text-xl leading-snug mb-2">
                      {post.title}
                    </h2>
                    <p className="text-slate-500 text-sm leading-relaxed line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="mt-4 flex items-center gap-1 text-[#2E4DB5] text-sm font-semibold group-hover:gap-2 transition-all">
                      {t.blog.read_more} <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
